<template>
    <ns-modal v-model="dialog" :title="txc.add_payment" :fullscreen="fullscreen" @input="closeDialog">
        <server-error v-if="!!dialogError" :error="dialogError"/>
        <v-form ref="paymentForm" v-model="formValid" @submit.prevent>
            <p>{{txc.add_a_payment_modal_description}}</p>
            <v-radio-group v-model="paymentMethods" row :mandatory="true">
                <v-radio
                    v-for="type in payTypes"
                    :key="type.id"
                    :id="type.id"
                    :value="type.name"
                    @change="cardChanged(type.name)"
                    name="ccInfoType"
                >
                    <template slot="label">{{type.localName}}</template>
                </v-radio>
            </v-radio-group>
            <div
                v-if="(paymentMethods=='VISA' || paymentMethods=='VISD'
                    || paymentMethods=='MC' || paymentMethods=='MCD'
                    || paymentMethods=='DISC' || paymentMethods=='AMEX'
                    || paymentMethods=='JCB' || paymentMethods=='DINE'
                    || paymentMethods=='MIR')"
            >
                <v-layout>
                    <v-flex xs12>
                        <div class="field-label">{{txc.name_on_card}}</div>
                        <v-text-field
                            name="fullName"
                            :rules="[ruleSet.required, ruleSet.alphaPatternSpace]"
                            v-model="fullName"
                            @keydown="noNumbers"
                        />
                    </v-flex>
                </v-layout>

                <v-layout>
                    <v-flex xs12>
                        <div class="field-label">{{txc.card_number}}</div>
                        <v-text-field
                            name="number"
                            v-model="number"
                            :mask="mask"
                            required
                            :rules="[rules.creditRules]"
                            pattern="[0-9]*"
                        />
                    </v-flex>
                </v-layout>

                <v-layout>
                    <v-flex xs6 lg2>
                        <div class="field-label">{{txc.month}}</div>
                        <v-text-field
                            name="expiryMonth"
                            v-model="expMonth"
                            :rules="[ruleSet.expireMonthRules]"
                            required
                            mask="##"
                        />
                    </v-flex>
                    <v-flex xs6 lg2>
                        <div class="field-label">{{txc.year}}</div>
                        <v-text-field
                            name="expiryYear"
                            v-model="expYear"
                            :rules="[ruleSet.expireYearRules]"
                            required
                            mask="##"
                        />
                    </v-flex>
                </v-layout>
            </div>
            <v-layout v-if="(paymentMethods=='SDD')">
                <v-flex xs12>
                    <div class="field-label">{{txc.iban_number}}</div>
                    <v-text-field name="number" v-model="paymentObj.number" pattern="[0-9]*"/>
                </v-flex>
            </v-layout>
            <v-layout v-if="(paymentMethods=='CEDN')">
                <v-flex xs12>
                    <div class="field-label">{{txc.card_number}}</div>
                    <v-text-field
                        name="number"
                        v-model="number"
                        :mask="mask"
                        required
                        :rules="[rules.creditRules]"
                        pattern="[0-9]*"
                    />
                </v-flex>
            </v-layout>
        </v-form>
        <template v-slot:actions>
            <v-layout column>
                <v-flex>
                    <button
                        flat
                        block
                        class="primary-hit mb-2 fluid"
                        :disabled="!formValid"
                        @click="addPayment()"
                    >{{sharedStrings.generic.save}}</button>
                </v-flex>
                <v-spacer/>
                <v-flex>
                    <button
                        flat
                        block
                        outline
                        class="secondary-hit fluid"
                        @click="closeDialog()"
                    >{{sharedStrings.generic.cancel}}</button>
                </v-flex>
            </v-layout>
        </template>
    </ns-modal>
</template>

<script>
import { PaymentType, PaymentService, PaymentDto } from '@nuskin/ns-shop';

import * as ruleSet from '../../repositories/globalRules.js';
import { mapGetters } from 'vuex';

import { NsModal } from '@nuskin/design-components';
import ServerError from '../ServerError';
import profileUtils from '../../repositories/profileUtils';

export default {
    components: {
        NsModal,
        ServerError
    },
    props: {
        payTypes: Array
    },
    data() {
        return {
            ruleSet,
            fullName: '',
            number: '',
            expMonth: '',
            expYear: '',
            formValid: '',
            paymentMethods: '',
            dialog: true,
            dialogError: null,
            mask: '#### #### #### ####',
            yearRule: [v => !!v || this.txc.fieldRequired],
            rules: {
                creditRules: ruleSet.visaCardRules
            }
        };
    },
    computed: {
        ...mapGetters({
            txc: 'translationsPayment',
            sharedStrings: 'sharedStrings',
            fullscreen: 'mobileView'
        })
    },
    methods: {
        closeDialog() {
            this.$emit('closeDialog');
            this.dialogError = null;
            if (this.$root.$el) {
                this.$root.$el.closest('body').classList.remove('no-scroll');
            }
        },
        cardChanged(cardName) {
            switch (cardName) {
                case 'VISA':
                case 'VISD':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.visaCardRules);
                    this.number = '';
                    break;
                case 'MC':
                case 'MCD':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.masterCardRules);
                    this.number = '';
                    break;
                case 'DISC':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.discCardRules);
                    this.number = '';
                    break;
                case 'AMEX':
                    this.mask = '#### ###### #####';
                    this.$set(this.rules, 'creditRules', ruleSet.amexCardRules);
                    this.number = '';
                    break;
                case 'JCB':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.jcbCardRules);
                    this.number = '';
                    break;
                case 'DINE':
                    this.mask = '#### ###### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.dinersCardRules);
                    this.number = '';
                    break;
                case 'MIR':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.mirCardRules);
                    this.number = '';
                    break;
                case 'CEDN':
                    this.mask = '###############';
                    this.$set(this.rules, 'creditRules', ruleSet.cednCardRules);
                    this.number = '';
                    break;
                case 'SDD':
                    this.mask = '#### #### #### ####';
                    this.$set(this.rules, 'creditRules', ruleSet.sepaCardRules);
                    this.number = '';
                    break;
            }
        },
        addPayment() {
            let self = this;
            let sendObj = {};

            if (this.paymentMethods === 'CEDN' || this.paymentMethods === 'SDD') {
                sendObj.paymentNumber = self.number;
                sendObj.paymentTypeId = self.getIdByType(this.paymentMethods);
            } else {
                sendObj.paymentName = self.fullName;
                sendObj.paymentNumber = self.number;
                sendObj.expMonth = self.expMonth;
                sendObj.expYear =
          self.expYear.length > 2 ? this.expYear.substr(-2) : this.expYear;
                sendObj.paymentTypeId = self.getIdByType(this.paymentMethods);
            }

            let dto = new PaymentDto(sendObj);

            PaymentService.addPayment(dto)
                .then(response => {
                    response.class = self.selectedType;
                    response.lastFour = response.paymentNumber.substr(-4);
                    this.$emit('addPaymentCard', response);
                    self.closeDialog();
                    profileUtils.publishAnalytics(
                        self.$analytics.category.payments,
                        'payment_method',
                        'added'
                    );
                })
                .catch(error => {
                    this.dialogError = error;
                });
        },
        getIdByType(type) {
            return PaymentType[type.toUpperCase()];
        },
        noNumbers(e) {
            // eslint-disable-next-line no-useless-escape
            if (e.key.match(/[A-Za-z \.]/g) == null) e.preventDefault();
        }
    },
    created() {
        profileUtils.publishAnalytics(
            this.$analytics.category.payments,
            'paymentMethodDialog',
            'open'
        );
    },
    destroyed() {
        profileUtils.publishAnalytics(
            this.$analytics.category.payments,
            'paymentMethodDialog',
            'close'
        );
    }
};
</script>

<style scoped>
.v-sheet {
  overflow: hidden;
}
</style>
